/**
 * The ONE place the gateway's two casings meet.
 *
 * `GET /liability` answers snake_case (`payable_cents`) because it comes from
 * `reconcile.liability_report`; every other gateway endpoint answers camelCase. The treasury
 * strip reads the first and the balances table reads the second, and they display THE SAME
 * NUMBERS — so without a single reconciliation point the two surfaces would drift into
 * showing different figures for one practice, which on a money screen is the worst kind of
 * bug: quiet, plausible, and wrong.
 *
 * Pure functions, no React, no axios — unit-testable, and tested byte-for-byte against each
 * other (`gatewayShapes.test.js`).
 *
 * THE BUCKETS ARE NOT INDEPENDENT POTS. From `ledger.py`:
 *
 *     payable  = tenant_payable + tenant_adjustments   (everything owed, hold or not)
 *     pending  = the part still inside the hold window
 *     available = payable − pending − reserved
 *     reserved = claimed by in-flight payout requests
 *
 * so `payable == pending + available + reserved`. Displaying payable BESIDE the other three
 * invites the eye to add all four; the strip therefore shows the three disjoint parts and
 * names the total as a total.
 *
 * ONE EXCEPTION, on `GET /liability` only: an address a practice deleted before D7 also
 * held. Its `payable_cents` is everything Solunary owes under the address, and the share
 * the predecessor left (`predecessor_payable_cents`) is withdrawable by nobody, so it is in
 * none of the three parts: `payable == pending + available + reserved + predecessor`
 * (D7 contract section 3.14). `tenant_balances` (`GET /balances`) is the holder's wallet,
 * where the identity holds as written.
 */

const int = (v) => (v == null ? null : Number(v) || 0);

/**
 * `liability.totals` (snake_case, keyed by currency) → one row per currency.
 * @returns {Array<{currency: string, onHoldCents: number|null, readyCents: number|null,
 *                  inTransitCents: number|null, owedCents: number|null,
 *                  debtCents: number|null}>}
 */
export function liabilityTotalsToBuckets(totals, { settlementFirst = "" } = {}) {
  const rows = Object.entries(totals || {}).map(([currency, t]) => ({
    currency,
    onHoldCents: int(t?.pending_cents),
    readyCents: int(t?.available_cents),
    inTransitCents: int(t?.reserved_cents),
    owedCents: int(t?.payable_cents),
    debtCents: int(t?.debt_cents),
  }));
  return sortCurrencies(rows, settlementFirst);
}

/**
 * One raw `tenant_balances` document (camelCase, from `GET /balances`) → the same shape.
 * `availableCents` is already net of the reserve in the stored doc, which is why it maps to
 * `readyCents` directly rather than being derived here.
 */
export function balanceDocToBuckets(doc) {
  return {
    tenantId: doc?.tenantId ?? null,
    currency: String(doc?.currency || ""),
    onHoldCents: int(doc?.pendingCents),
    readyCents: int(doc?.availableCents),
    inTransitCents: int(doc?.reservedCents),
    owedCents: int(doc?.payableCents),
    debtCents: int(doc?.debtCents),
    frozen: Boolean(doc?.frozen),
    frozenReason: doc?.frozenReason ?? null,
  };
}

/** One `liability.tenants` row (snake_case) → the same shape, so the balances table can be
 *  fed from either endpoint without the caller knowing which. */
export function liabilityTenantToBuckets(row) {
  return {
    tenantId: row?.tenant_id ?? null,
    currency: String(row?.currency || ""),
    onHoldCents: int(row?.pending_cents),
    readyCents: int(row?.available_cents),
    inTransitCents: int(row?.reserved_cents),
    owedCents: int(row?.payable_cents),
    debtCents: int(row?.debt_cents),
    frozen: Boolean(row?.frozen),
    frozenReason: null,
  };
}

/** Settlement currency first (it is the one the ledger balances in), then alphabetical.
 *  Never a cross-currency total row — the design document forbids summing across them. */
export function sortCurrencies(rows, settlementFirst = "") {
  const s = String(settlementFirst || "").toLowerCase();
  return [...rows].sort((a, b) => {
    const ac = a.currency.toLowerCase();
    const bc = b.currency.toLowerCase();
    if (ac === s && bc !== s) return -1;
    if (bc === s && ac !== s) return 1;
    return ac.localeCompare(bc);
  });
}
